import React, { useState } from 'react';
import styled from 'styled-components';
import { FaWaveSquare, FaMoneyBillWave, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

const PaymentContainer = styled.div`
  padding: 2rem;
  background: #f8f9fa;
  border-radius: 12px;
  margin: 2rem 0;
`;

const PaymentTitle = styled.h3`
  color: #333;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  font-weight: 600;
`;

const PaymentOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PaymentOption = styled.label`
  display: flex;
  align-items: center;
  padding: 1.2rem;
  background: white;
  border: 2px solid ${props => props.selected ? '#FF6B35' : '#e9ecef'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #FF6B35;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.15);
  }
`;

const RadioInput = styled.input`
  margin-right: 1rem;
  width: 20px;
  height: 20px;
  accent-color: #FF6B35;
`;

const PaymentInfo = styled.div`
  flex: 1;
`;

const PaymentName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

const PaymentDescription = styled.div`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const PaymentIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #FF6B35, #F7931E);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  margin-left: 1rem;
  
  img {
    width: 30px;
    height: 30px;
    object-fit: contain;
  }
`;

const WaveDetails = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #856404;
`;

const NotificationBanner = styled.div`
  padding: 1rem;
  background: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : '#f5c6cb'};
  border-radius: 6px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
  font-size: 0.9rem;
`;

const PaymentFeatures = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const FeatureTag = styled.span`
  padding: 0.25rem 0.5rem;
  background: #e9ecef;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const PhoneInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  margin-top: 1rem;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #FF6B35;
  }
`;

const PaymentMethod = ({ selectedMethod, onMethodChange, customerInfo, onCustomerInfoChange }) => {
  const [notification, setNotification] = useState(null);
  const [phoneError, setPhoneError] = useState('');

  const paymentMethods = [
    {
      id: 'wave',
      name: 'Wave',
      description: 'Paiement instantané et sécurisé via Wave',
      icon: <img src="/logo/wave.jpg" alt="Wave" />,
      details: 'Vous serez redirigé vers l\'application Wave pour finaliser le paiement. Le paiement est immédiat et vous recevrez une confirmation par SMS et email.',
      features: ['Instantané', 'Sécurisé', 'SMS/Email', 'QR Code'],
      requiresPhone: true
    },
    {
      id: 'cash_on_delivery',
      name: 'Paiement après livraison',
      description: 'Payez lorsque vous recevez votre commande',
      icon: <FaMoneyBillWave size={24} />,
      details: 'Payez en espèces ou par mobile money lorsque le livreur vous remet votre commande. Vous recevrez une confirmation de commande par SMS et email.',
      features: ['À la livraison', 'SMS/Email', 'Suivi commande'],
      requiresPhone: true
    }
  ];

  const validatePhone = (phone) => {
    // Validation des numéros CI
    const phoneRegex = /^(\+225|00225)?(0[1-7]\d{8}|[1-7]\d{8})$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const handlePhoneChange = (e) => {
    const phone = e.target.value;
    setPhoneError('');
    
    if (phone && !validatePhone(phone)) {
      setPhoneError('Format invalide. Ex: 07XX XX XX XX, 05XX XX XX XX ou +22507XX XX XX XX');
    }
    
    onCustomerInfoChange({ ...customerInfo, phone: formatPhone(phone) });
  };

  const formatPhone = (phone) => {
    // Formater automatiquement le numéro
    let cleaned = phone.replace(/\s/g, '');
    if (cleaned.startsWith('00225')) {
      cleaned = cleaned.replace(/^00225/, '+225');
    }
    if (cleaned.length === 10 && !cleaned.startsWith('+225')) {
      return '+225' + cleaned;
    }
    return cleaned;
  };

  return (
    <PaymentContainer>
      <PaymentTitle>Choisir un moyen de paiement</PaymentTitle>
      
      {notification && (
        <NotificationBanner type={notification.type}>
          {notification.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
          {notification.message}
        </NotificationBanner>
      )}
      
      <PaymentOptions>
        {paymentMethods.map(method => (
          <PaymentOption
            key={method.id}
            selected={selectedMethod === method.id}
          >
            <RadioInput
              type="radio"
              name="payment_method"
              value={method.id}
              checked={selectedMethod === method.id}
              onChange={() => onMethodChange(method.id)}
            />
            <PaymentInfo>
              <PaymentName>{method.name}</PaymentName>
              <PaymentDescription>{method.description}</PaymentDescription>
              
              <PaymentFeatures>
                {method.features.map(feature => (
                  <FeatureTag key={feature}>
                    {feature}
                  </FeatureTag>
                ))}
              </PaymentFeatures>
              
              {selectedMethod === method.id && (
                <WaveDetails>
                  {method.details}
                  
                  {method.requiresPhone && (
                    <div>
                      <PhoneInput
                        type="tel"
                        placeholder="Numéro de téléphone (ex: 07XX XX XX XX)"
                        value={customerInfo?.phone || ''}
                        onChange={handlePhoneChange}
                        style={{ marginTop: '1rem' }}
                      />
                      {phoneError && (
                        <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                          {phoneError}
                        </div>
                      )}
                    </div>
                  )}
                </WaveDetails>
              )}
            </PaymentInfo>
            <PaymentIcon>{method.icon}</PaymentIcon>
          </PaymentOption>
        ))}
      </PaymentOptions>
    </PaymentContainer>
  );
};

export default PaymentMethod;